import { Fragment } from "react";

import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";

import {
  NodeFieldLabel,
  type LabelPlacement,
} from "@/components/doc/right-sidebar/active-node-style/fields/NodeTextField";
import { ExpressionNode } from "@/components/expression/ExpressionNode";
import * as Node from "@/components/expression/utils/layout";
import { useCursorContextId } from "@/components/input/CursorContextProvider";
import { twMerge } from "@/utils/twMerge";

import type { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import type { ComponentPropsWithoutRef } from "react";

type Props = {
  node: InlineNodeModel | InlineNodeModel[];
  label?: string;
  action?: React.ReactNode;
  onLabelMouseDown?: () => void;
  className?: string;
  actionClassName?: string;
  contentClassName?: string;
  labelPlacement?: LabelPlacement;
  disableBlur?: boolean;
} & ComponentPropsWithoutRef<"div">;

export const InlineNodeField = observer(function InlineNodeField({
  node: inlineNode,
  label,
  action,
  className,
  onLabelMouseDown = () => {},
  labelPlacement,
  actionClassName,
  contentClassName,
  disableBlur,
  ...props
}: Props) {
  const contextId = useCursorContextId();
  const nodes = Array.isArray(inlineNode) ? inlineNode : [inlineNode];
  const [node] = nodes;

  if (!node) {
    return null;
  }

  const { doc } = node;

  const handleLabelMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    onLabelMouseDown();

    runInAction(() => {
      doc.cursorContextId = contextId;
      doc.selection.resetSelection();
    });

    queueMicrotask(() => {
      const lastNode = nodes[nodes.length - 1];
      doc.cursor.moveTo(lastNode, lastNode?.nodes.length ?? 0);
    });
  };

  const handleLabelBlur = () => {
    if (disableBlur) return;

    doc.transact(
      () => {
        doc.selection.resetSelection();
        doc.cursorContextId = undefined;
      },
      "InlineNodeField:labelClick",
      { runInAction: true },
    );
  };

  return (
    <NodeFieldLabel
      as="div"
      label={label}
      className={twMerge("py-0.5 pr-0.5 min-h-[28px] flex-1 min-w-0 flex-wrap", className)}
      onMouseDown={handleLabelMouseDown}
      onBlur={handleLabelBlur}
      labelClassName="py-1 pr-1"
      labelPlacement={labelPlacement}
      {...props}
    >
      <div
        className={twMerge(
          "px-1 py-[1px] font-mono overflow-auto no-scrollbar flex items-baseline",
          action && "pr-6",
          contentClassName,
        )}
      >
        {nodes.map((n, index) => (
          <Fragment key={n.id}>
            <ExpressionNode node={n} className="flex-none self-baseline" />
            {index !== nodes.length - 1 && (
              <Node.Value className="self-center relative z-10">,</Node.Value>
            )}
          </Fragment>
        ))}
      </div>
      {action && (
        <div className={twMerge("absolute right-0 top-0 bottom-0 p-0.5 pl-0", actionClassName)}>
          {action}
        </div>
      )}
    </NodeFieldLabel>
  );
});
